// StatusChangePendingModal — manual status flip between 'pending' and
// 'final_notice'. The legacy page exposes this through a clickable status
// pill on each row.
//
// Server contract: PUT /pending/:enc_id with { status }. The state machine
// allows pending → final_notice (and final_notice → pending is rejected by
// the server). When the user picks a status the row is already in, the
// modal closes silently (no-op).

import { Clock, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { useToast } from '../../../hooks/useToast';
import { useUpdatePending } from '../hooks/usePending';
import type { PendingStatus } from '../../../types/api';
import styles from './StatusChangePendingModal.module.css';

export interface StatusChangePendingModalProps {
  open: boolean;
  encId: string | null;
  currentStatus: PendingStatus | null;
  onClose: () => void;
  onChanged?: () => void;
}

const OPTIONS: ReadonlyArray<{
  status: 'pending' | 'final_notice';
  label: string;
  description: string;
}> = [
  {
    status: 'pending',
    label: 'Pending',
    description: 'Regular pending status — awaiting customer response',
  },
  {
    status: 'final_notice',
    label: 'Final Notice',
    description: 'Urgent — customer has 24 hours to respond',
  },
];

export function StatusChangePendingModal({
  open,
  encId,
  currentStatus,
  onClose,
  onChanged,
}: StatusChangePendingModalProps): JSX.Element | null {
  const toast = useToast();
  const update = useUpdatePending(encId ?? undefined);

  if (!open) return null;

  async function pick(status: 'pending' | 'final_notice') {
    if (!encId) return;
    if (status === currentStatus) {
      onClose();
      return;
    }
    try {
      await update.mutateAsync({ status });
      toast.success(`Status changed to ${labelOf(status)}.`);
      onChanged?.();
      onClose();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not change status.'
        : 'Could not change status.';
      toast.error(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={update.isPending ? () => undefined : onClose}
      title="Change Booking Status"
      size="sm"
      preventClose={update.isPending}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
      }
    >
      <p className={styles.subtitle}>Select the new status for this pending booking.</p>
      <div className={styles.options}>
        {OPTIONS.map((opt) => (
          <button
            key={opt.status}
            type="button"
            className={
              opt.status === currentStatus ? styles.optionActive : styles.option
            }
            onClick={() => void pick(opt.status)}
            disabled={update.isPending}
          >
            <span
              className={
                opt.status === 'pending' ? styles.iconPending : styles.iconFinalNotice
              }
              aria-hidden
            >
              {opt.status === 'pending' ? <Clock size={18} /> : <AlertTriangle size={18} />}
            </span>
            <span className={styles.optionContent}>
              <span className={styles.optionLabel}>{opt.label}</span>
              <span className={styles.optionDesc}>{opt.description}</span>
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

function labelOf(status: 'pending' | 'final_notice'): string {
  return status === 'pending' ? 'Pending' : 'Final Notice';
}
